import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageProvider';
import { translations } from '../i18n/translations';

const AboutPage: React.FC = () => {
  const { language } = useLanguage();
  const dict = translations[language].aboutPage;


  // Mini case studies
  const miniCases = [
    {
      logo: '/TUe-logo-square-descriptor-stack-scarlet-L.png',
      outcome: '90% tijd bespaard bij studentbeoordeling',
      cta: '→ Bekijk case'
    },
    {
      logo: '/cropped-maps-untold-logo-3-1-4-scaled-1.png',
      outcome: '10x snellere stadsgids personalisatie',
      cta: '→ Bekijk case'
    },
    {
      logo: '/renewable.png',
      outcome: '100% geautomatiseerde klant onboarding',
      cta: '→ Bekijk case'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-sand py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold text-navy mb-6 font-space">
              {dict.heroTitle}
            </h1>
            <p className="text-xl md:text-2xl text-slate mb-8 leading-relaxed">{dict.heroSub}</p>
          </div>
        </div>
      </section>

      {/* Why AgentTailor Exists & Pattern Section - Side by Side */}
      <section className="py-24 bg-white">
        <div className="max-w-[72rem] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left: Why AgentTailor Exists */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-navy mb-6 font-space">
                {dict.whyExistsTitle}
              </h2>
              <p className="text-lg text-slate leading-relaxed">
                {dict.whyExistsContent}
              </p>
            </div>
            
            {/* Right: The Pattern We Kept Seeing */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-navy mb-6 font-space">
                {dict.patternTitle}
              </h2>
              <p className="text-lg text-slate leading-relaxed">
                {dict.patternContent}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why It Works - 3 Card Grid */}
      <section className="py-24 bg-sand">
        <div className="max-w-[72rem] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-navy mb-6 font-space">
              {dict.whyWorksTitle}
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {dict.whyWorksCards.map((card, index) => {
              const icons = [
                <img src="/sewing-tailor-svgrepo-com.svg" alt="Custom tailored solutions" className="w-16 h-16" />,
                <img src="/flow-tree-svgrepo-com.svg" alt="Integration flow" className="w-16 h-16" />,
                <img src="/privacy-dashboard-svgrepo-com.svg" alt="Privacy and security" className="w-16 h-16" />
              ];
              
              return (
                <div key={index} className="bg-white rounded-xl p-12 text-center shadow-sm hover:shadow-md transition-all duration-300 h-full flex flex-col">
                  <div className="flex justify-center mb-8">
                    <div className="p-6 bg-sky/10 rounded-full text-navy">
                      {icons[index]}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-navy mb-6 font-space">
                    {card.title}
                  </h3>
                  <p className="text-slate leading-relaxed flex-grow">
                    {card.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Proof Bar */}
      <section className="py-16 bg-white">
        <div className="max-w-[72rem] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-navy mb-6 font-space">
              {dict.proofBar.title}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
            <div>
              <div className="text-4xl md:text-5xl font-bold text-navy mb-2 font-space">
                {dict.proofBar.metric1.number}
              </div>
              <div className="text-slate">
                {dict.proofBar.metric1.label}
              </div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-navy mb-2 font-space">
                {dict.proofBar.metric2.number}
              </div>
              <div className="text-slate">
                {dict.proofBar.metric2.label}
              </div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-navy mb-2 font-space">
                {dict.proofBar.metric3.number}
              </div>
              <div className="text-slate">
                {dict.proofBar.metric3.label}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mini Case Studies */}
      <section className="py-24 bg-sand">
        <div className="max-w-[72rem] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-navy mb-6 font-space">
              Resultaten in de praktijk
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {miniCases.map((caseItem, index) => (
              <Link 
                key={index}
                to="/case-studies"
                className="bg-white rounded-xl p-8 text-center hover:shadow-md transition-all duration-300 group"
              >
                <div className="mb-6">
                  <img 
                    src={caseItem.logo} 
                    alt="Client logo" 
                    className="h-16 mx-auto object-contain opacity-80 group-hover:opacity-100 transition-opacity" 
                  />
                </div>
                <p className="text-slate mb-4 leading-relaxed">
                  {caseItem.outcome}
                </p>
                <div className="text-sky font-semibold group-hover:text-navy transition-colors">
                  {caseItem.cta}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="py-24 bg-navy">
        <div className="max-w-[72rem] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-sand mb-6 font-space">
            Klaar om te beginnen?
          </h2>
          <p className="text-lg text-sand/80 mb-12 max-w-2xl mx-auto leading-relaxed">
            Plan een vrijblijvend gesprek en ontdek hoe we jouw workflows kunnen automatiseren.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact#calendly"
              className="bg-sky text-navy px-8 py-4 rounded-lg font-semibold hover:bg-sky/90 transition-colors duration-200 inline-block"
            >
              Plan een kennismaking
            </Link>
            <a 
              href="mailto:info@agenttailor.ai"
              className="text-sand/90 hover:text-sand transition-colors duration-200 px-8 py-4"
            >
              Mail ons
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;